import React, { RefObject, useMemo } from 'react';
import {
  ConfigProvider,
  Select as AntSelect,
  SelectProps as AntSelectProps,
  RefSelectProps,
  Tag,
  TagProps,
} from 'antd';
import type { Variant as AntdVariant } from 'antd/es/config-provider';
import type { InputStatus } from 'antd/es/_util/statusUtils';
import AntLibSelect, { SelectValue } from 'antd/lib/select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import styled, { css, createGlobalStyle, useTheme } from 'styled-components';
import { buildNextSelectTheme } from '../next/tokens';

export type { RefSelectProps } from 'antd';

export type { SelectValue } from 'antd/lib/select';

const { Option, OptGroup } = AntLibSelect;

export type { DefaultOptionType, SelectProps as BaseSelectProps } from 'antd/es/select';

const UpDownCaretsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5.33203 5.33366L7.99936 2.66699L10.6667 5.33366"
      stroke="currentColor"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M5.33203 10.6663L7.99936 13.333L10.6667 10.6663"
      stroke="currentColor"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);

export type SelectFieldVariant = 'badge' | 'next';

export interface SelectProps<T> extends Omit<AntSelectProps<T>, 'variant'> {
  hasError?: boolean;
  width?: string;
  variant?: SelectFieldVariant | AntdVariant;
  inputRef?: RefObject<RefSelectProps>;
}

const CUSTOM_VARIANTS: readonly SelectFieldVariant[] = ['badge', 'next'];
const isCustomVariant = (
  variant: SelectFieldVariant | AntdVariant | undefined,
): variant is SelectFieldVariant =>
  variant !== undefined && (CUSTOM_VARIANTS as readonly string[]).includes(variant);

const variantBadgeEditableStyles = css(
  ({ theme }) => `
  width: auto!important;
  
  .ant-select-arrow {
    color: ${theme.token.colorPrimary}!important;
  }
  
  .ant-select-selection-item {
    color: ${theme.token.colorPrimary}!important;
    font-weight: 600;
   
  }
`,
);

const variantBadgeDisabledStyles = css(
  ({ theme }) => `
  width: auto!important;

  .ant-select-arrow {
    color: ${theme.token.colorPrimary}!important;
  }

  .ant-select-selection-item {
    color: ${theme.token['green.6']}!important;
    font-weight: 600;
  }

  .ant-select-selector {
    background-color: ${theme.token['green.1']}!important;
    border-color: ${theme.token['green.3']}!important;
  }
`,
);

/**
 * Minimal CSS for the "next" variant — only for things ConfigProvider tokens can't express.
 * Everything else (height, border-radius, colors, focus ring, error ring, multi-item styling)
 * is handled by the ConfigProvider theme in buildNextSelectTheme(token.next).
 */
const variantNextMinimalStyles = css`
  /* Hover: match Input hover (Select has no hoverBorderColor token).
   * Uses !important on border-color to beat Ant's selector specificity. */
  &:hover:not(.ant-select-disabled):not(.ant-select-focused):not(.ant-select-status-error)
    .ant-select-selector {
    border-color: ${({ theme }) => theme.token.next.colorBorderHover} !important;
    box-shadow: ${({ theme }) => theme.token.next.boxShadowHover};
  }

  /* Focus and (Focus + hover): keep focus styles (exclude error state) */
  &.ant-select-focused:not(.ant-select-status-error) .ant-select-selector,
  &.ant-select-focused:not(.ant-select-status-error):hover .ant-select-selector {
    border-color: ${({ theme }) => theme.token.next.colorBorderFocus} !important;
    box-shadow: ${({ theme }) => theme.token.next.boxShadowFocus} !important;
  }

  /* Error: keep error border */
  &.ant-select-status-error .ant-select-selector {
    border-color: ${({ theme }) => theme.token.next.colorError} !important;
  }

  /* Error + hover / Error + focus / Error + focus + hover: use error shadow */
  &.ant-select-status-error:hover:not(.ant-select-disabled) .ant-select-selector,
  &.ant-select-status-error.ant-select-focused .ant-select-selector {
    border-color: ${({ theme }) => theme.token.next.colorError} !important;
    box-shadow: ${({ theme }) => theme.token.next.boxShadowError} !important;
  }

  /* Disabled: border matches bg (Ant keeps border visible on disabled) */
  &.ant-select-disabled .ant-select-selector {
    border-color: ${({ theme }) => theme.token.next.colorBgDisabled} !important;
    box-shadow: none !important;
  }

  /* Disabled with value: font-weight 500; placeholder stays at default 400 */
  &.ant-select-disabled .ant-select-selection-item {
    font-weight: ${({ theme }) => theme.token.next.fontWeight500} !important;
  }

  /* Flex layout for selected item — only when label contains a child element
   * (icon + text). Plain-text labels stay display: block so Ant's
   * text-overflow: ellipsis keeps working (ellipsis doesn't apply to anonymous
   * text inside a flex container).
   *
   * Nests through &.ant-select-single + .ant-select-selector to reach (0,4,0)
   * specificity — needed to beat Ant's size-variant reset chain
   * :where(...).ant-select-single.ant-select-sm .ant-select-selector .ant-select-selection-item
   * (also 0,4,0; styled-components loads after Ant, so source order wins). */
  &.ant-select-single .ant-select-selector .ant-select-selection-item:has(> *) {
    display: flex;
    align-items: center;
    align-self: center;
    gap: 4px;
    min-width: 0;
  }
`;

const NEXT_DROPDOWN_CLASS = 'ant-select-dropdown-next';

/**
 * Minimal dropdown CSS — only for chrome that Ant doesn't expose as tokens.
 * Item height, padding, font-size, selected/active bg are all handled by ConfigProvider.
 */
const NextSelectDropdownStyles = createGlobalStyle`
  .${NEXT_DROPDOWN_CLASS}.ant-select-dropdown {
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.token.next.colorBorder};
    padding: 6px;

    .ant-select-item {
      color: ${({ theme }) => theme.token.next.palette.grey800};

      &.ant-select-item-option-disabled {
        color: ${({ theme }) => theme.token.next.palette.grey400};
      }
    }

    /* Plain-text option contents keep Ant's defaults (block + ellipsis).
     * Only icon+text option contents need flex so the icon aligns with the label. */
    .ant-select-item-option-content {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ant-select-item-option-content:has(> *) {
      display: flex;
      align-items: center;
      gap: 4px;
      min-width: 0;
    }

    .ant-select-item-option-state {
      flex-shrink: 0;
      margin-left: 8px;
    }

    .ant-select-item-option-selected .ant-select-item-option-state {
      color: ${({ theme }) => theme.token.next.colorText};
    }

    /* A selected option must highlight identically to an unselected one. Ant zeroes
     * the selected bg (optionSelectedBg), which otherwise wins over the active bg
     * during keyboard nav — and this rule previously used the stale grey50 token
     * (invisible in dark) instead of the theme-tuned option-active bg. Match the
     * -active class (mouse hover + keyboard) so both modes stay consistent. */
    .ant-select-item-option-selected:not(.ant-select-item-option-disabled).ant-select-item-option-active,
    .ant-select-item-option-selected:not(.ant-select-item-option-disabled):hover {
      background-color: ${({ theme }) => theme.token.next.colorBgOptionActive};
    }
  }
`;

const StyledSelect = styled(AntSelect)<{ $variant?: SelectFieldVariant; disabled?: boolean }>`
  --fa-animation-duration: 0.8s;

  .ant-select-selection-item {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* A disabled select that still holds a value is displaying committed
     configuration the user needs to read, not an inactive control. The rETL
     mapping screens disable Object, Sync mode, Event type and the identifier
     mappings permanently once a connection exists. Ant inks those with
     colorTextDisabled, which lands at 2.58:1 on the disabled surface.
     The mid-grey reads at 7.85:1 in dark and 6.01:1 in light — still clearly
     softer than an enabled field, but legible. Disabled-ness stays legible
     from the muted background, flattened border and missing caret. */
  &.ant-select-disabled .ant-select-selection-item {
    color: ${({ theme }) => theme.token.next.palette.grey600};
  }

  .ant-select-arrow {
    color: ${({ theme, disabled }) =>
      disabled ? theme.token.colorTextDisabled : theme.token.colorTextBase};
  }

  ${({ $variant, disabled }) =>
    $variant === 'badge' && (disabled ? variantBadgeDisabledStyles : variantBadgeEditableStyles)};

  ${({ $variant }) => $variant === 'next' && variantNextMinimalStyles};
` as unknown as <T extends SelectValue = SelectValue>(
  props: AntSelectProps<T> & { $variant?: SelectFieldVariant; ref?: React.Ref<RefSelectProps> },
) => React.ReactElement;

type VariantPropsResult<T> = Partial<Omit<SelectProps<T>, 'variant'>> & {
  antdVariant?: AntdVariant;
};

const getVariantProps = <T extends SelectValue = SelectValue>(
  variant?: SelectFieldVariant,
  status?: InputStatus,
  disabled?: boolean,
): VariantPropsResult<T> => {
  const variantProps: VariantPropsResult<T> = {};

  switch (variant) {
    case 'badge':
      variantProps.className = `ant-select-variant-${variant}`;
      variantProps.antdVariant = disabled || status ? 'outlined' : 'borderless';
      variantProps.suffixIcon = disabled ? null : (
        <FontAwesomeIcon icon={solid('caret-down')} size="lg" />
      );
      break;
    case 'next':
      variantProps.className = `ant-select-variant-${variant}`;
      variantProps.classNames = { popup: { root: NEXT_DROPDOWN_CLASS } };
      variantProps.menuItemSelectedIcon = <FontAwesomeIcon icon={regular('check')} />;
      break;
    default:
      break;
  }

  return variantProps;
};

const Select = <T extends SelectValue = SelectValue>({
  popupMatchSelectWidth = false,
  hasError,
  status,
  width = '100%',
  variant,
  size = variant === 'next' ? 'middle' : 'large',
  inputRef,
  ...rest
}: SelectProps<T>) => {
  const { token } = useTheme();
  const nextTheme = useMemo(() => buildNextSelectTheme(token.next), [token.next]);
  const customVariant = isCustomVariant(variant) ? variant : undefined;
  const passThroughAntdVariant = !isCustomVariant(variant) ? variant : undefined;
  const { antdVariant: variantPropsAntdVariant, ...variantProps } = getVariantProps<T>(
    customVariant,
    status,
    rest?.disabled,
  );
  const resolvedAntdVariant = variantPropsAntdVariant ?? passThroughAntdVariant;

  /* eslint-disable no-nested-ternary */
  const defaultSuffixIcon = rest.loading ? (
    <FontAwesomeIcon icon={regular('spinner-third')} spin speed="0.1s" />
  ) : customVariant === 'next' && rest.open === false ? null : customVariant === 'next' ? (
    <UpDownCaretsIcon />
  ) : (
    <FontAwesomeIcon icon={solid('caret-down')} />
  );
  // Honor consumer's explicit `suffixIcon` (including `null` to suppress the arrow,
  // replacing antd's deprecated `showArrow={false}`); fall back to default otherwise.
  const suffixIcon = 'suffixIcon' in rest ? rest.suffixIcon : defaultSuffixIcon;

  const selectElement = (
    <>
      {customVariant === 'next' && <NextSelectDropdownStyles />}
      <StyledSelect
        size={size}
        status={hasError ? 'error' : status}
        popupMatchSelectWidth={popupMatchSelectWidth}
        {...rest}
        $variant={customVariant}
        {...variantProps}
        {...(resolvedAntdVariant ? { variant: resolvedAntdVariant } : {})}
        className={[variantProps.className, rest.className].filter(Boolean).join(' ') || undefined}
        style={{ width, ...rest.style }}
        suffixIcon={suffixIcon}
        ref={inputRef}
      />
    </>
  );

  if (customVariant === 'next') {
    return <ConfigProvider theme={nextTheme}>{selectElement}</ConfigProvider>;
  }

  return selectElement;
};

const SelectTag: React.FC<TagProps> = (props) => (
  <Tag closable closeIcon={<FontAwesomeIcon icon={regular('close')} />} {...props} />
);

export { Option, Select, SelectTag, OptGroup };
